import { useQuery } from '@tanstack/react-query'
import { BellRing, CheckCheck, Pin } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Pagination } from '@/components/tables/Pagination'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { Switch } from '@/components/ui/Switch'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useListParams } from '@/hooks/useListParams'
import { cn } from '@/utils/cn'
import { feedQuery, unreadCountQuery, useMarkAllRead } from '../api'
import { categoryMeta } from '../meta'
import { ANNOUNCEMENT_CATEGORIES, type FeedItem } from '../types'
import { FeedCard } from './FeedCard'

const FILTERS = ['category', 'unread'] as const

/** The recipient notice board shared by the staff, family and admin portals. */
export function NoticesFeed({ homeHref, title = 'Notices' }: { homeHref: string; title?: string }) {
  const { t } = useTranslation('announcements')
  useDocumentTitle(title)
  const { state, update, setPage } = useListParams(FILTERS, { perPage: 15 })
  const unreadOnly = state.filters.unread === '1'
  const feed = useQuery(
    feedQuery({
      page: state.page,
      perPage: state.perPage,
      category: state.filters.category,
      unread: unreadOnly,
    }),
  )
  const unread = useQuery(unreadCountQuery(true))
  const markAll = useMarkAllRead()

  const items = feed.data?.data ?? []
  const pinned = items.filter((i) => i.isPinned)
  const rest = items.filter((i) => !i.isPinned)
  const unreadCount = unread.data ?? 0

  return (
    <>
      <PageHeader
        title={t(title)}
        description={t('News and notices from the school, newest first. Open a notice to mark it as read.')}
        breadcrumbs={[{ label: t(title) }]}
        homeHref={homeHref}
        meta={
          unreadCount > 0 ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-100">
              <BellRing className="size-3.5" aria-hidden="true" />{' '}
              {t('{{count}} unread', { count: unreadCount })}
            </span>
          ) : null
        }
        actions={
          <Button
            variant="secondary"
            leftIcon={<CheckCheck />}
            disabled={unreadCount === 0}
            loading={markAll.isPending}
            onClick={() =>
              markAll.mutate(undefined, {
                onSuccess: () => toast.success(t('All caught up'), t('Every notice is marked as read.')),
              })
            }
          >
            {t('Mark all as read')}
          </Button>
        }
      />

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div
          role="group"
          aria-label={t('Filter by category')}
          className="-mx-1 flex scrollbar-thin gap-1.5 overflow-x-auto px-1 pb-1"
        >
          <FilterChip active={!state.filters.category} onClick={() => update({ category: undefined })}>
            {t('All')}
          </FilterChip>
          {ANNOUNCEMENT_CATEGORIES.map((c) => {
            const Icon = categoryMeta[c].icon
            return (
              <FilterChip
                key={c}
                active={state.filters.category === c}
                onClick={() => update({ category: state.filters.category === c ? undefined : c })}
              >
                <Icon className="size-3.5" aria-hidden="true" /> {t(categoryMeta[c].label)}
              </FilterChip>
            )
          })}
        </div>
        <div className="flex shrink-0 items-center gap-2 text-sm text-ink-soft">
          <Switch
            id="notices-unread-only"
            checked={unreadOnly}
            onCheckedChange={(v) => update({ unread: v ? '1' : undefined })}
          />
          <label htmlFor="notices-unread-only">{t('Unread only')}</label>
        </div>
      </div>

      {feed.isPending ? (
        <FeedSkeleton />
      ) : feed.error ? (
        <Card>
          <ErrorState onRetry={() => void feed.refetch()} />
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<BellRing />}
            title={unreadOnly ? t("You're all caught up") : t('No notices yet')}
            description={
              unreadOnly
                ? t('There are no unread notices.')
                : state.filters.category
                  ? t('Nothing in this category right now.')
                  : t('Announcements from the school will appear here.')
            }
          />
        </Card>
      ) : (
        <div className="space-y-8">
          {pinned.length ? <FeedSection title={t('Pinned')} icon={<Pin />} items={pinned} /> : null}
          {rest.length ? <FeedSection title={pinned.length ? t('Latest') : undefined} items={rest} /> : null}
        </div>
      )}
      <Pagination meta={feed.data?.meta} onPageChange={setPage} className="mt-6" />
    </>
  )
}

function FeedSection({ title, icon, items }: { title?: string; icon?: ReactNode; items: FeedItem[] }) {
  const { t } = useTranslation('announcements')
  return (
    <section aria-label={title ?? t('Notices')}>
      {title ? (
        <h2 className="mb-3 flex items-center gap-2 text-xs font-semibold tracking-wide text-muted uppercase [&_svg]:size-3.5">
          {icon}
          {title}
        </h2>
      ) : null}
      <ul className="space-y-3">
        {items.map((item) => (
          <li key={item.id}>
            <FeedCard item={item} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ring-1 transition-colors ring-inset',
        active
          ? 'bg-brand-600 text-white ring-brand-600'
          : 'bg-surface text-ink-soft ring-line hover:bg-surface-muted',
      )}
    >
      {children}
    </button>
  )
}

function FeedSkeleton() {
  const { t } = useTranslation('announcements')
  return (
    <ul className="space-y-3" aria-busy="true" aria-label={t('Loading notices')}>
      {Array.from({ length: 4 }, (_, i) => (
        <li key={i} className="flex gap-3.5 rounded-card border border-line bg-surface p-5">
          <Skeleton className="size-10 rounded-xl" />
          <div className="flex-1 space-y-2.5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-full" />
          </div>
        </li>
      ))}
    </ul>
  )
}
